import React, { useContext, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { FilterContext } from "../../context/Filter";

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  if (isNaN(date)) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const DynamicTanStackTable = ({ momTableValues }) => {
  const { open } = useContext(FilterContext);

  // Transform backend response to flat rows
  const transformData = (values) => {
    const transformedRows = [];

    values.forEach((item) => {
      const selfResponses = item.self_responses?.response_docs?.hits?.hits || [];
      const otherResponses =
        item.other_responses?.response_docs?.hits?.hits || [];

      const firstSelfResponse = selfResponses?.[0]?._source;
      const firstOtherResponse = otherResponses?.[0]?._source;

      const baseSource = firstSelfResponse || firstOtherResponse;

      const allSelfResponsesText = selfResponses
        .map((hit) => hit._source?.RESPONSE)
        .filter(Boolean)
        .join("\n\n");

      const allOtherResponsesText = otherResponses
        .map((hit) => hit._source?.RESPONSE)
        .filter(Boolean)
        .join("\n\n");

      const row = {
        KA_CODE: baseSource?.KA_CODE || "",
        DISCUSSION_ID: baseSource?.DISCUSSION_ID || item.key,
        MEETING_DATE: baseSource?.MEETING_DATE
          ? formatDate(baseSource.MEETING_DATE)
          : "",
        CREATED_BY_USER_ID: baseSource?.CREATED_BY_USER_ID || "",
        CREATED_BY_NAME: baseSource?.CREATED_BY_NAME || "",
        CREATED_BY_POSITION_TYPE: baseSource?.CREATED_BY_POSITION_TYPE || "",
        REASSIGN_STATUS: baseSource?.REASSIGN_STATUS || "",
        REASSIGN_BY_NAME: baseSource?.REASSIGN_BY_NAME || "",
        REASSIGN_BY_POSITION_TYPE: baseSource?.REASSIGN_BY_POSITION_TYPE || "",
        REASSIGN_COUNT: baseSource?.REASSIGN_COUNT || 0,
        EXPECTED_COMPLETION_DATE: baseSource?.EXPECTED_COMPLETION_DATE
          ? formatDate(baseSource.EXPECTED_COMPLETION_DATE)
          : "",
        RAISER_UPLOADED_FILE: baseSource?.RAISER_UPLOADED_FILE || "",
        RESPONSIBLE_UPLOADED_FILE: baseSource?.RESPONSIBLE_UPLOADED_FILE || "",

        RESPONSE_SELF: allSelfResponsesText || null,
        NO_OF_RESPONSES_SELF: item.self_responses?.doc_count || 0,
        RESPONSE_BY_NAME_SELF: firstSelfResponse?.RESPONSE_BY_NAME || "",
        RESPONSE_BY_POSITION_TYPE_SELF:
          firstSelfResponse?.RESPONSE_BY_POSITION_TYPE || "",

        RESPONSE_OTHER: allOtherResponsesText || null,
        NO_OF_RESPONSES_OTHER: item.other_responses?.doc_count || 0,
        RESPONSE_BY_NAME_OTHER: firstOtherResponse?.RESPONSE_BY_NAME || "",
        RESPONSE_BY_POSITION_TYPE_OTHER:
          firstOtherResponse?.RESPONSE_BY_POSITION_TYPE || "",
      };

      transformedRows.push(row);
    });

    return transformedRows;
  };

  // Memoized transformation
  const tableData = useMemo(() => {
    const safeMomTableValues = Array.isArray(momTableValues)
      ? momTableValues
      : [];
    const isValidStructure = safeMomTableValues.every(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        "key" in item &&
        (item.self_responses === undefined ||
          typeof item.self_responses === "object") &&
        (item.other_responses === undefined ||
          typeof item.other_responses === "object")
    );

    if (!isValidStructure) {
      return [];
    }

    return transformData(safeMomTableValues);
  }, [momTableValues]);

  const columnHelper = useMemo(() => createColumnHelper(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor("KA_CODE", { header: "KA Code" }),
      columnHelper.accessor("DISCUSSION_ID", { header: "Discussion ID" }),
      columnHelper.accessor("MEETING_DATE", { header: "Meeting Date" }),
      columnHelper.accessor("CREATED_BY_USER_ID", {
        header: "Created By User ID",
      }),
      columnHelper.accessor("CREATED_BY_NAME", { header: "Created By Name" }),
      columnHelper.accessor("CREATED_BY_POSITION_TYPE", {
        header: "Created By Position",
      }),
      columnHelper.accessor("REASSIGN_STATUS", { header: "Reassign Status" }),
      columnHelper.accessor("REASSIGN_BY_NAME", { header: "Reassign By Name" }),
      columnHelper.accessor("REASSIGN_BY_POSITION_TYPE", {
        header: "Reassign By Position",
      }),
      columnHelper.accessor("REASSIGN_COUNT", { header: "Reassign Count" }),
      columnHelper.accessor("EXPECTED_COMPLETION_DATE", {
        header: "Expected Completion Date",
      }),

      columnHelper.accessor("RESPONSE_SELF", {
        header: "Responsible Remark",
        cell: (info) => {
          const responses = info.getValue();
          if (!responses) return null;
          const responseList = responses.split("\n\n").filter(Boolean);
          return (
            <div className="max-h-20 overflow-y-auto w-64" title={responses}>
              <ol className="list-decimal list-inside">
                {responseList.map((response, idx) => (
                  <li key={idx} className="mb-1">
                    {response}
                  </li>
                ))}
              </ol>
            </div>
          );
        },
        size: 256,
      }),

      columnHelper.accessor("NO_OF_RESPONSES_SELF", {
        header: "No. of Responsible Remark",
      }),
      columnHelper.accessor("RESPONSE_BY_NAME_SELF", {
        header: "Response By Name",
      }),
      columnHelper.accessor("RESPONSE_BY_POSITION_TYPE_SELF", {
        header: "Responsible Response By Position",
      }),

      columnHelper.accessor("RESPONSE_OTHER", {
        header: "Non-Responsible Remark",
        cell: (info) => {
          const responses = info.getValue();
          if (!responses) return null;
          const responseList = responses.split("\n\n").filter(Boolean);
          return (
            <div className="max-h-20 overflow-y-auto w-64" title={responses}>
              <ol className="list-decimal list-inside">
                {responseList.map((response, idx) => (
                  <li key={idx} className="mb-1">
                    {response}
                  </li>
                ))}
              </ol>
            </div>
          );
        },
        size: 256,
      }),

      columnHelper.accessor("NO_OF_RESPONSES_OTHER", {
        header: "No. of Non-Responsible Responses",
      }),
      columnHelper.accessor("RAISER_UPLOADED_FILE", {
        header: "Raiser Upload File",
      }),
      columnHelper.accessor("RESPONSIBLE_UPLOADED_FILE", {
        header: "Responsible Upload File",
      }),
    ],
    [columnHelper]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // ✅ Empty state (covers invalid or empty data)
  if (!tableData.length) {
    return (
      <div className="text-sm text-red-400 text-center mt-10">
        No Data Available for this ARN.
      </div>
    );
  }

  return (
    <div className="p-4 w-full">
      <p className="p-1 py-1 px-2 bg-[#056DB3] text-white font-bold rounded-t-md">
        MoM Reports
      </p>
      <div className="mt-2 mb-2 text-sm text-gray-600">
        Total records: {tableData.length}
      </div>

      <div
        className={`max-h-[360px] overflow-x-auto overflow-y-auto border border-gray-200 ${open ? "w-[1000px]" : "w-full"
          }`}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="sticky top-0 bg-gray-50 px-2 py-1 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-200 last:border-r-0"
                    style={{ width: header.getSize(), zIndex: 10 }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.map((row, index) => (
              <tr
                key={row.original.DISCUSSION_ID || row.id}
                className={`text-xs ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-2 py-1 text-xs text-gray-900 border-r border-gray-200 last:border-r-0"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DynamicTanStackTable;
